// #4942 / #4994 follow-up (df-6bc15f1a946e): keep the record of recent couch-login starts
// somewhere that SURVIVES A PAGE RELOAD.
//
// This is the whole reason the storm detector can work at all. Both measured storms were
// page-reload loops — the web view kept reloading its own page and the server saw one login
// per load — so every login came from a fresh JavaScript context. A counter held in memory
// would have reset on each reload and counted 1 every time. See couch-login-storm.ts's
// header for the rest of the reasoning.
//
// sessionStorage, not localStorage, for three reasons:
//  - it survives a reload, which is the case that matters;
//  - it dies with the tab, so a 10-second window can never contain yesterday's starts;
//  - it is per tab, so two browser tabs each running a legitimate poll do not add up and
//    look like one device storming.
//
// It is cleared by the sign-out wipe (clearSessionStorageAndCookies, on the clearLocalData
// path only), which is correct: this is a per-user diagnostic and the next account on the
// device should not inherit it. An ordinary in-app reload does NOT clear it, so a burst that
// spans one of those is still counted whole.

import {
  CouchLoginStart,
  CouchLoginStormState,
  LOGIN_START_RING_CAPACITY,
  appendLoginStart,
  emptyCouchLoginStormState,
} from './couch-login-storm';

/** Where the record lives. Versioned, so a shape change cannot be misread as valid data. */
export const COUCH_LOGIN_STORM_KEY = 'cs_couch_login_starts_v1';

/** The parts of `sessionStorage` this module uses, so it can be tested without a browser. */
export interface CouchLoginStormStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

/** Why a read or a write did not work, for the caller to report. Null means it worked. */
export type CouchLoginStormStoreFailure =
  // storage refused or is unavailable — Safari private mode, a full quota, a blocked origin
  | 'storage-threw'
  // something is under our key that this version cannot read
  | 'unreadable-record'
  | null;

/** What a read produced: always a usable state, plus what went wrong if anything did. */
export interface CouchLoginStormReadResult {
  state: CouchLoginStormState;
  failure: CouchLoginStormStoreFailure;
}

/**
 * Read the record. Never throws: a device that cannot store this must still be able to log
 * in, so every failure resolves to an empty record and is REPORTED rather than swallowed —
 * the caller turns `failure` into a silent problem.
 */
export function readCouchLoginStormState(storage: CouchLoginStormStorage): CouchLoginStormReadResult {
  let raw: string | null;
  try {
    raw = storage.getItem(COUCH_LOGIN_STORM_KEY);
  } catch {
    return { state: emptyCouchLoginStormState(), failure: 'storage-threw' };
  }
  // if nothing has been written yet, that is the ordinary first-login case, not a failure
  if (raw == null) return { state: emptyCouchLoginStormState(), failure: null };

  try {
    const parsed = JSON.parse(raw);
    if (!parsed || !Array.isArray(parsed.starts)) {
      return { state: emptyCouchLoginStormState(), failure: 'unreadable-record' };
    }
    // drop any entry that is not shaped like a start — a half-written record must not make
    // the arithmetic below produce a nonsense report
    const starts: CouchLoginStart[] = parsed.starts.filter(
      (s: unknown): s is CouchLoginStart =>
        !!s
        && typeof (s as CouchLoginStart).atMs === 'number'
        && typeof (s as CouchLoginStart).reason === 'string'
        && typeof (s as CouchLoginStart).contextId === 'string',
    );
    return {
      state: {
        starts: starts.slice(-LOGIN_START_RING_CAPACITY),
        lastReportedAtMs: typeof parsed.lastReportedAtMs === 'number' ? parsed.lastReportedAtMs : null,
        reportedThroughMs: typeof parsed.reportedThroughMs === 'number' ? parsed.reportedThroughMs : null,
      },
      failure: starts.length === parsed.starts.length ? null : 'unreadable-record',
    };
  } catch {
    return { state: emptyCouchLoginStormState(), failure: 'unreadable-record' };
  }
}

/** Write the record back. Never throws; answers what went wrong so the caller can report it. */
export function writeCouchLoginStormState(
  storage: CouchLoginStormStorage,
  state: CouchLoginStormState,
): CouchLoginStormStoreFailure {
  try {
    storage.setItem(COUCH_LOGIN_STORM_KEY, JSON.stringify(state));
    return null;
  } catch {
    return 'storage-threw';
  }
}

/** Read, add one start, write back. Returns the state as it now stands, plus any failure. */
export function recordCouchLoginStart(
  storage: CouchLoginStormStorage,
  start: CouchLoginStart,
): CouchLoginStormReadResult {
  const read = readCouchLoginStormState(storage);
  const state: CouchLoginStormState = { ...read.state, starts: appendLoginStart(read.state.starts, start) };
  const writeFailure = writeCouchLoginStormState(storage, state);
  return { state, failure: read.failure ?? writeFailure };
}

/**
 * Mark a storm as reported, BEFORE the report is actually sent.
 *
 * The order matters and is copied from how the launch link is handled (`f453c1187`): a send
 * that navigates the page away loses a mark written after it, and in a reload storm that
 * would file one report per reload — hundreds of them.
 */
export function markCouchLoginStormReported(
  storage: CouchLoginStormStorage,
  reportedAtMs: number,
  reportedThroughMs: number,
): CouchLoginStormStoreFailure {
  const read = readCouchLoginStormState(storage);
  return writeCouchLoginStormState(storage, {
    ...read.state,
    lastReportedAtMs: reportedAtMs,
    reportedThroughMs,
  });
}
